import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Building2, MapPin, Star, Eye } from "lucide-react";
import { useFormatDate } from "../../utils/dateFormat";
import { getLocalized } from "../../utils/localized";

export default function VenuePreviewPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const fmt = useFormatDate();
  const [venue, setVenue] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [hours, setHours] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venueRes, galleryRes, hoursRes] = await Promise.all([
          axios.get(`${API}/owner/venue`, { withCredentials: true }),
          axios.get(`${API}/owner/gallery`, { withCredentials: true }),
          axios.get(`${API}/owner/opening-hours`, { withCredentials: true }),
        ]);
        setVenue(venueRes.data);
        setGallery(galleryRes.data.filter(p => p.is_active));
        setHours(hoursRes.data);

        if (venueRes.data?.venue_id) {
          const revRes = await axios.get(`${API}/venues/${venueRes.data.venue_id}/reviews?limit=3`);
          setReviews(revRes.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!venue) return <p className="text-muted-foreground text-center py-10">{t("owner_venue:no_venue")}</p>;

  const venueName = getLocalized(venue, "name", lang) || venue.name;
  const venueDescShort = getLocalized(venue, "description_short", lang) || venue.description_short;
  const venueDescLong = getLocalized(venue, "description_long", lang) || venue.description_long;
  const venueAddress = getLocalized(venue, "address", lang) || venue.address;
  const venueCity = getLocalized(venue, "city_name", lang) || venue.city_name;

  return (
    <div data-testid="venue-preview-page">
      {/* Preview Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-5 py-3 mb-6 flex items-center gap-3">
        <Eye className="h-5 w-5 text-amber-600 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{t("owner_preview:banner")}</p>
          <p className="text-xs text-amber-600 dark:text-amber-400">{t("owner_preview:desc")}</p>
        </div>
        <Link to="/owner/venue">
          <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs">
            {t("common:edit") || "Edit"}
          </Button>
        </Link>
      </div>

      {/* Hero Section */}
      <div className="relative rounded-xl overflow-hidden mb-6 bg-gradient-to-br from-slate-800 to-slate-900" style={{ height: 280 }}>
        {gallery.length > 0 ? (
          <img src={`${API.replace("/api", "")}${gallery[0].url}`} alt={venueName} className="w-full h-full object-cover opacity-80" />
        ) : venue.hero_image ? (
          <img src={venue.hero_image} alt={venueName} className="w-full h-full object-cover opacity-80" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 className="h-16 w-16 text-white/30" />
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
          <h1 className="text-3xl font-semibold text-white">{venueName}</h1>
          <div className="flex items-center gap-4 mt-2 text-white/80 text-sm">
            {venueCity && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{venueCity}</span>}
            {venue.average_review_score && (
              <span className="flex items-center gap-1"><Star className="h-4 w-4 text-amber-400 fill-amber-400" />{venue.average_review_score} ({venue.review_count})</span>
            )}
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Description + Gallery */}
        <div className="lg:col-span-2 space-y-6">
          {venueDescShort && (
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-3">{t("venue:about")}</h2>
              <p className="text-muted-foreground leading-relaxed">{venueDescShort}</p>
              {venueDescLong && <p className="text-muted-foreground leading-relaxed mt-3">{venueDescLong}</p>}
            </div>
          )}

          {gallery.length > 0 && (
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-3">{t("owner_gallery:title")}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {gallery.slice(0, 6).map(photo => (
                  <div key={photo.photo_id} className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
                    <img src={`${API.replace("/api", "")}${photo.url}`} alt={photo.title || venueName} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              {gallery.length > 6 && (
                <p className="text-sm text-muted-foreground text-center mt-3">{t("owner_preview:more_photos", { count: gallery.length - 6 })}</p>
              )}
            </div>
          )}

          {reviews.length > 0 && (
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-3">{t("venue:reviews")}</h2>
              <div className="space-y-4">
                {reviews.map(r => (
                  <div key={r.review_id} className="border-b border-border pb-3 last:border-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{r.user_name}</span>
                      <span className="flex items-center text-xs text-amber-500"><Star className="h-3 w-3 fill-amber-400 mr-0.5" />{r.score}/10</span>
                    </div>
                    {r.review_text && <p className="text-sm text-muted-foreground">{r.review_text}</p>}
                    {r.owner_reply && (
                      <div className="mt-2 ml-4 pl-3 border-l-2 border-primary/30">
                        <p className="text-xs text-muted-foreground italic">{r.owner_reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Contact & Info */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h2 className="text-lg font-medium text-foreground mb-4">{t("venue:details")}</h2>
            <div className="space-y-3 text-sm">
              {venueAddress && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">{venueAddress}</span>
                </div>
              )}
              {venue.phone && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">{venue.phone}</span>
                </div>
              )}
              {venue.email && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">{venue.email}</span>
                </div>
              )}
              {venue.website && (
                <div className="flex items-center gap-2">
                  <span className="text-primary text-xs truncate">{venue.website}</span>
                </div>
              )}
            </div>
          </div>

          {/* Opening Hours */}
          {hours.length > 0 && (
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-4">{t("owner_availability:opening_hours")}</h2>
              <div className="space-y-2 text-sm">
                {hours.map((h, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-muted-foreground">{fmt(new Date(2024, 0, 1 + h.day_of_week), "EEEE")}</span>
                    <span className={h.is_closed ? "text-red-500" : "text-foreground"}>
                      {h.is_closed ? t("owner_calendar:legend_closed") : `${h.open_time} - ${h.close_time}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Booking CTA (preview only) */}
          <div className="bg-card rounded-xl border border-border p-6">
            <Button className="w-full" disabled data-testid="preview-book-btn">
              {t("booking:book_now")}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">{t("owner_preview:booking_note")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
